import { motion } from "framer-motion";
import PageBanner from "@/components/PageBanner";
import { Info, Sun, Heart, Camera, Car, Coins, Shirt, Users, Hotel, PawPrint } from "lucide-react";

const sections = [
  {
    title: "Climate",
    icon: Sun,
    content: "Uganda, known as the pearl of Africa, boasts beautiful weather year-round, with average temperatures ranging from 21°C to 31°C. The country experiences two rainy seasons, from March to May and October to November. Temperatures in mountainous areas may drop to 10°C in certain months. Fortunately, national parks, lodges, and other facilities are open year-round, making any time of the year suitable for visiting Uganda."
  },
  {
    title: "Health & Medical",
    icon: Heart,
    content: "Please bring all personal medications required, as clients are fully responsible for securing adequate medical insurance. It is highly recommended that clients cover their travel insurance to protect themselves from heavy medical costs. We use AMREF Flying Doctors, offering evacuation cover, aimed at providing air evacuation services for members with serious medical conditions. Malaria is a serious and sometimes fatal disease transmitted by mosquitoes; precautions are essential, including avoiding mosquito bites, using insect repellents, and sleeping under a mosquito net when necessary. Consult your doctor for suitable anti-malarial tablets."
  },
  {
    title: "Photography",
    icon: Camera,
    content: "It is only polite to ask people before taking photographs, but note that photographs of military buildings or soldiers are not allowed."
  },
  {
    title: "Safari Equipment & Gadgets",
    icon: Car,
    content: `Handy devices and gadgets needed on safari include: Camera, Charger, Extra batteries, Memory card, Binoculars. A 3-pin UK-style adapter or a convertible adapter will be useful for charging.`
  },
  {
    title: "Currency",
    icon: Coins,
    content: "The currency in Uganda is the Uganda Shilling. US dollars, GB pounds, and euros are readily exchangeable. Large US dollar bills attract the best exchange rates."
  },
  {
    title: "Clothing",
    icon: Shirt,
    content: "Cotton clothing, augmented by a cardigan or jacket, should be adequate during your stay in Uganda. In mountainous areas, waterproof jackets, walking boots, and heavy sweaters will be essential."
  },
  {
    title: "Accommodation",
    icon: Hotel,
    content: "Electricity and piped water supply may not always be available, but clients will always find a hot shower waiting for them after a day's activity. Food includes fresh produce and a variety of tropical fruits."
  },
  {
    title: "Gorilla Tracking",
    icon: PawPrint,
    content: "Gorilla tracking is a tiring activity, requiring physical fitness. Uganda Wildlife Authority ranger guides will lead the group, and porters are available to carry personal belongings at a small fee. The minimum trekking age is 15 years."
  }
];

const gorillaRules = {
  general: [
    "Always wash your hands before visiting the gorillas.",
    "A maximum of eight visitors may visit a group of habituated mountain gorillas in a day.",
    "Follow the guides to the gorillas' location, and keep your voices low.",
    "Look out for gorilla nesting sites along the way.",
    "Keep a safe distance and avoid leaving rubbish in the park.",
    "Wear proper hiking attire, including long sleeves and sturdy boots.",
    "Minimum age for gorilla trekking is 15 years old."
  ],
  withGorillas: [
    "Keep your voice low and ask questions quietly.",
    "Stay in a tight group and maintain a 7-meter distance from the gorillas.",
    "Avoid eating or drinking near the gorillas.",
    "If a gorilla charges, crouch down slowly and avoid eye contact.",
    "Do not touch the gorillas or use flash photography.",
    "Move slowly and Socially when taking pictures."
  ],
  health: [
    "Mountain gorillas share 98% of our DNA and are susceptible to human diseases.",
    "If you are feeling ill or have a contagious disease, please stay behind.",
    "Cover your nose and mouth when coughing or sneezing near the gorillas.",
    "Respect the time limit imposed on visits to minimize disease transmission and stress."
  ]
};

export default function TravelInfo() {
  return (
    <div className="min-h-screen bg-gray-50">
      <PageBanner
        title="Travel Information"
        description="Essential information for your East African safari adventure"
        backgroundImage="/Uganda-Wildlife-Safari.png"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <section.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {section.title}
                  </h3>
                </div>
                <div className="space-y-4">
                  {section.content.split('\n').map((paragraph, i) => (
                    <p
                      key={i}
                      className="text-gray-600 leading-relaxed"
                    >
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Gorilla Trekking Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Gorilla Trekking Information
            </h2>
            <p className="text-lg text-gray-600">
              Approximately 800 mountain gorillas exist globally in the wild today, found in Rwanda, DRC, and Uganda. 
              They are known as highland gorillas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
              <h3 className="text-xl font-semibold mb-6 text-gray-900">Basic Rules</h3>
              <ul className="space-y-3">
                {gorillaRules.general.map((rule, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
              <h3 className="text-xl font-semibold mb-6 text-gray-900">With the Gorillas</h3>
              <ul className="space-y-3">
                {gorillaRules.withGorillas.map((rule, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
              <h3 className="text-xl font-semibold mb-6 text-gray-900">Health Guidelines</h3>
              <ul className="space-y-3">
                {gorillaRules.health.map((rule, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-xl shadow-md p-8">
            <h3 className="text-xl font-semibold mb-6 text-gray-900">Conservation Contribution</h3>
            <p className="text-gray-600 mb-6">
              By following the rules and purchasing a permit, you contribute to the conservation of the mountain gorilla. 
              A percentage of funds raised from park entrance fees supports local communities and natural resource management.
            </p>
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-red-700 font-medium">Important Reminder</p>
              <p className="text-red-600 mt-1">
                Any breach of these rules may lead to the termination of tracking without a refund.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}